import { Entity } from '@backstage/catalog-model';
import { parseLocationRef } from '@backstage/catalog-model';
import { CombinedReleaseHistory, HealthStatus } from '@internal/backstage-plugin-ewii-azure';

// Types
export interface VersionInfo {
  version: string;
  componentName: string;
  buildDate?: string;
  changeLogUrl?: string;
  changeLogFile?: string;
  commit?: string;
  branch?: string;
  healthStatus?: HealthStatus[];
  hasReleaseHistory?: boolean;
}

export interface ServiceRow {
  serviceName: string;
  systemName: string;
  test?: VersionInfo;
  acceptance?: VersionInfo;
  production?: VersionInfo;
  [key: string]: string | VersionInfo | undefined;
}

// Constants
export const ENVIRONMENTS = ['test', 'acceptance', 'production'] as const;
export type Environment = typeof ENVIRONMENTS[number];

export const RELEASE_HISTORY_ANNOTATION = 'ewii/release-history-url';

// Date utilities
export const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-GB', {
    timeZone: 'Europe/Copenhagen',
    dateStyle: 'short',
    timeStyle: 'medium'
  });
};

export const isRecent = (buildDate?: string, hours: number = 24): boolean => {
  if (!buildDate) return false;
  const now = new Date();
  const build = new Date(buildDate);
  const diff = now.getTime() - build.getTime();
  return diff <= hours * 60 * 60 * 1000;
};

export const isRecent7Days = (buildDate?: string) => isRecent(buildDate, 7 * 24);
export const isRecent30Days = (buildDate?: string) => isRecent(buildDate, 30 * 24);

// Entity processing utilities
export const getSystemTitles = (systems: Entity[]): Record<string, string> => {
  return systems.reduce((acc, system) => {
    acc[system.metadata.name] = system.metadata.title || system.metadata.name;
    return acc;
  }, {} as Record<string, string>);
};

// Utility function to extract base filename from path or URL
export const extractBaseFileName = (input: string): string => {
  // Handle both file paths and URLs
  let fileName: string;
  
  if (input.includes('://')) {
    // It's a URL - extract from path parameter
    try {
      const url = new URL(input);
      const pathParam = url.searchParams.get('path');
      if (pathParam) {
        const segments = pathParam.split(/[\\/]/);
        fileName = segments[segments.length - 1];
      } else {
        // fallback: try to extract from the end of the URL
        const match = input.match(/([^/\\?&]+)\.md/);
        fileName = match ? match[1] : input;
      }
    } catch {
      // fallback: try to extract from the end of the string
      const match = input.match(/([^/\\?&]+)\.md/);
      fileName = match ? match[1] : input;
    }
  } else {
    // It's a file path - extract the last segment
    const segments = input.split(/[\\/]/);
    fileName = segments[segments.length - 1];
  }
  
  // Remove .md extension if present
  return fileName.replace(/\.md$/, '');
};

export const getVersionInfo = (entity: Entity): { version: string; buildDate?: string; changeLogUrl?: string; changeLogFile?: string; commit?: string; branch?: string } => {
  const version = typeof entity.metadata?.annotations?.['ewii/build'] === 'string' 
    ? entity.metadata.annotations['ewii/build']
    : '';
  const buildDate = typeof entity.metadata?.annotations?.['ewii/build-date'] === 'string'
    ? entity.metadata.annotations['ewii/build-date']
    : undefined;
  const changeLogUrl = typeof entity.metadata?.annotations?.['ewii/change-log-url'] === 'string'
    ? entity.metadata.annotations['ewii/change-log-url']
    : undefined;
  
  let changeLogFile: string | undefined = undefined;
  
  // First try to get from the change-log-file annotation
  if (typeof entity.metadata?.annotations?.['ewii/change-log-file'] === 'string') {
    const annotation = entity.metadata.annotations['ewii/change-log-file'];
    changeLogFile = extractBaseFileName(annotation);
  } 
  // Fallback to extracting from the change-log-url
  else if (changeLogUrl) {
    changeLogFile = extractBaseFileName(changeLogUrl);
  }
  
  const commit = entity.metadata?.annotations?.['ewii/commit'];
  const branch = entity.metadata?.annotations?.['ewii/branch'];
  return { version, buildDate, changeLogUrl, changeLogFile, commit, branch };
};

export const processComponent = (
  entity: Entity,
  services: Record<string, ServiceRow>,
  systemTitles: Record<string, string>,
  envSet: Set<string>,
  healthStatusService: HealthStatus[]
) => {
  const name = entity.metadata.name;
  const title = entity.metadata.title || name;
  
  const env = typeof entity.spec?.lifecycle === 'string' 
    ? entity.spec.lifecycle.toLowerCase()
    : 'unknown';
  
  if (!ENVIRONMENTS.includes(env as Environment)) {
    return;
  }

  envSet.add(env);
  
  const systemName = entity.spec?.system as string || '';
  const serviceName = systemTitles[systemName] || title;
  const hasReleaseHistory = Boolean(entity.metadata?.annotations?.[RELEASE_HISTORY_ANNOTATION]);

  const rowKey = `${serviceName} (${title})`;

  if (!services[rowKey]) {
    services[rowKey] = { 
      serviceName: serviceName,
      systemName: systemName
    };
  }
  
  const { version, buildDate, changeLogUrl, changeLogFile, commit, branch } = getVersionInfo(entity);

  const existingVersion = services[rowKey][env];
  if (typeof existingVersion === 'object' && existingVersion.version) {
    const existingVersionNum = existingVersion.version.split('.').map(Number);
    const newVersionNum = version.split('.').map(Number);
    if (newVersionNum > existingVersionNum) {
      services[rowKey][env] = {
        version,
        componentName: name,
        buildDate,
        changeLogUrl,
        changeLogFile,
        commit,
        branch,
        healthStatus: healthStatusService.filter(status => status.component === name) ?? [],
        hasReleaseHistory: hasReleaseHistory
      };
    }
  } else {
    services[rowKey][env] = {
      version,
      componentName: name,
      buildDate,
      changeLogUrl,
      changeLogFile,
      commit,
      branch,
      healthStatus: healthStatusService.filter(status => status.component === name) ?? [],
      hasReleaseHistory: hasReleaseHistory
    };
  }
};

// Warning utilities
export const shouldShowWarning = (cell: VersionInfo, compareCell: VersionInfo | undefined): boolean => {
  if (!cell.commit) return false;
  
  if (!compareCell || !compareCell.commit) {
    return false;
  }
  
  return compareCell.commit !== cell.commit;
};

export const getWarningTooltip = (env: string, cell: VersionInfo, compareCell: VersionInfo | undefined): string => {
  const compareEnv = env === 'production' ? 'acceptance' : 'test';
  
  if (!compareCell || !compareCell.commit) {
    return `No commit found in ${compareEnv} environment`;
  }
  
  return `Commit in ${env} (${cell.commit}) differs from ${compareEnv} (${compareCell.commit})`;
};

// Summary utilities
export const getReleaseSummary = (releaseHistory: Record<string, CombinedReleaseHistory[]>) => {
  const last24Hours = {
    test: releaseHistory['test_24h']?.length || 0,
    acceptance: releaseHistory['acceptance_24h']?.length || 0,
    production: releaseHistory['production_24h']?.length || 0
  };
  
  const last7Days = {
    test: releaseHistory['test_7d']?.length || 0,
    acceptance: releaseHistory['acceptance_7d']?.length || 0,
    production: releaseHistory['production_7d']?.length || 0
  };

  const last30Days = {
    test: releaseHistory['test_30d']?.length || 0,
    acceptance: releaseHistory['acceptance_30d']?.length || 0,
    production: releaseHistory['production_30d']?.length || 0
  };

  return { last24Hours, last7Days, last30Days };
};

export const getHealthSummary = (services: ServiceRow[]) => {
  const healthStatus = {
    test: { healthy: 0, unhealthy: 0 },
    acceptance: { healthy: 0, unhealthy: 0 },
    production: { healthy: 0, unhealthy: 0 }
  };

  services.forEach(row => {
    ENVIRONMENTS.forEach(env => {
      const cell = row[env];
      if (typeof cell === 'object' && cell.healthStatus) {
        const hasUnhealthy = cell.healthStatus.some(status => status.status === 'Unhealthy');
        if (hasUnhealthy) {
          healthStatus[env as keyof typeof healthStatus].unhealthy++;
        } else if (cell.healthStatus.length > 0) {
          healthStatus[env as keyof typeof healthStatus].healthy++;
        }
      }
    });
  });

  return healthStatus;
};

// URL utilities
export const getCommitUrl = (entity: Entity, release: CombinedReleaseHistory): string => {
  const repoLocation = entity.metadata.annotations?.['backstage.io/source-location'];
  if(!repoLocation) {
    return '';
  }
  const repoUrl = parseLocationRef(repoLocation).target;

  const {fromBranch, toBranch, fromCommit, toCommit} = release;
  if(fromBranch == toBranch) {
    return `${repoUrl}/branchCompare?baseVersion=GC${release.fromCommit}&targetVersion=GC${release.toCommit}&_a=files`.trim();
  }
  return `${repoUrl}/branchCompare?baseVersion=GB${release.fromBranch}&targetVersion=GB${release.toBranch}&_a=files`.trim();
};

export const getBranchUrl = (entity: Entity, branchName: string, commitId: string): string => {
  const repoLocation = entity.metadata.annotations?.['backstage.io/source-location'];
  if(!repoLocation) {
    return '';
  }
  const repoUrl = parseLocationRef(repoLocation).target;
  if(branchName == 'main') {
    return `${repoUrl}?version=GC${commitId}`;
  }
  return `${repoUrl}?version=GB${branchName}`;
};

// Shared markdown styles for consistent styling across components
export const createMarkdownStyles = (theme: any) => ({
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  '& h1': {
    fontSize: '1.5rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(0.5),
  },
  '& h2': {
    fontSize: '1.25rem',
  },
  '& h3': {
    fontSize: '1.1rem',
  },
  '& p': {
    marginBottom: theme.spacing(1),
    lineHeight: 1.6,
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': {
      color: theme.palette.primary.dark,
      textDecoration: 'underline',
    },
    '&:visited': {
      color: theme.palette.primary.dark,
    },
  },
  '& ul, & ol': {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  '& li': {
    marginBottom: theme.spacing(0.5),
  },
  '& code': {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    padding: '2px 4px',
    borderRadius: '3px',
    fontFamily: 'monospace',
    fontSize: '0.9em',
  },
  '& pre': {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    padding: theme.spacing(1),
    borderRadius: '4px',
    overflow: 'auto',
    marginBottom: theme.spacing(1),
    '& code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    marginLeft: 0,
    paddingLeft: theme.spacing(2),
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.5, 1),
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(2, 0),
  },
});

// Helper function to extract change log file name from various sources
export const getChangeLogFileName = (changeLogFile?: string, changeLogUrl?: string): string => {
  if (changeLogFile) {
    return extractBaseFileName(changeLogFile);
  }
  if (changeLogUrl) {
    return extractBaseFileName(changeLogUrl);
  }
  return '';
}; 